import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import { Search as SearchIcon, ArrowBack as ArrowBackIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';

function Conversations() {
  const { sessionId } = useParams();
  const navigate = useNavigate();

  // Function to format number with commas
  const formatNumber = (num) => num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  const handleBack = () => {
    navigate(-1);
  };
  
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);

  const handleRowClick = (id) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  const fetchConversations = async () => {
    if (!sessionId) return;
    
    setLoading(true);
    setExpandedRow(null); // Reset expanded row on refresh
    try {
      const conversationsRef = collection(db, 'bot-session-transactions');
      const q = query(
        conversationsRef,
        where('sessionId', '==', sessionId),
        orderBy('dateTime', 'asc')
      );

      const querySnapshot = await getDocs(q);
      const results = [];
      querySnapshot.forEach(doc => {
        results.push({ id: doc.id, ...doc.data() });
      });

      // Sort conversations by date in descending order
      //const sortedResults = results.sort((a, b) => new Date(b.dateTime) - new Date(a.dateTime));
      setConversations(results);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConversations();
  }, [sessionId]);

  return (
    <Box p={3}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton 
            onClick={handleBack}
            sx={{ 
              mr: 2,
              padding: 1,
              borderRadius: 1,
              width: 'fit-content'
            }}
            aria-label="back"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4">
            Session Details
          </Typography>
        </Box>
        <IconButton 
          onClick={fetchConversations}
          disabled={loading}
          size="large"
          sx={{ 
            padding: 1,
            borderRadius: 1,
            width: 'fit-content'
          }}
          aria-label="refresh"
        >
          <RefreshIcon />
        </IconButton>
      </Box>
      {!loading && conversations.length === 0 ? (
        <Typography variant="body1" sx={{ textAlign: 'center', mt: 4 }}>
          No conversations found
        </Typography>
      ) : (
        <TableContainer component={Paper} sx={{ maxWidth: '100%', overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Transaction ID</TableCell>
                <TableCell>Date/Time</TableCell>
                <TableCell>Prompt Name</TableCell>
                <TableCell>User Message</TableCell>
                <TableCell>Response</TableCell>
                <TableCell align="right">LLM Requests</TableCell>
                <TableCell align="right">Candidates Tokens</TableCell>
                <TableCell align="right">Prompt Tokens</TableCell>
                <TableCell align="right">Total Tokens</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {conversations.map((conversation) => (
                <React.Fragment key={conversation.id}>
                  <TableRow 
                    onClick={() => handleRowClick(conversation.id)}
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                  >
                    <TableCell sx={{ verticalAlign: 'top', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {conversation.id}
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top' }}>
                      {new Date(conversation.dateTime).toLocaleString()}
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top' }}>
                      {conversation.promptName || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 300, whiteSpace: 'pre-wrap', verticalAlign: 'top' }}>
                      {conversation.userUtterance || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 300, verticalAlign: 'top' }}>
                      <Box sx={{ p: 1 }}>
                        <ReactMarkdown>{conversation.response || 'N/A'}</ReactMarkdown>
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                      {conversation.totalLlmRequests || '0'}
                    </TableCell>
                    <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                      {formatNumber(conversation.candidatesTokenCount || 0)}
                    </TableCell>
                    <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                      {formatNumber(conversation.promptTokenCount || 0)}
                    </TableCell>
                    <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                      {formatNumber(conversation.totalTokenCount || 0)}
                    </TableCell>
                  </TableRow>
                  {expandedRow === conversation.id && (
                    <TableRow>
                      <TableCell colSpan={9} sx={{ py: 2, bgcolor: 'action.selected' }}>
                        <Box sx={{ 
                          display: 'grid', 
                          gridTemplateColumns: 'minmax(0, 0.7fr) minmax(0, 0.3fr)',
                          gap: 2,
                          px: 2,
                          width: '100%'
                        }}>
                          <Box>
                            <Typography variant="subtitle2" gutterBottom>
                              System Instructions:
                            </Typography>
                            <Paper sx={{ p: 2, bgcolor: 'background.paper' }}>
                              <ReactMarkdown>{conversation.systemInstructions || 'N/A'}</ReactMarkdown>
                            </Paper>
                          </Box>
                          <Box>
                            <Typography variant="subtitle2" gutterBottom>
                              Prompt:
                            </Typography>
                            <Paper sx={{ p: 2, bgcolor: 'background.paper' }}>
                              <ReactMarkdown>{conversation.prompt || 'N/A'}</ReactMarkdown>
                            </Paper>
                          </Box>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

export default Conversations;
